import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ClockIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  LockClosedIcon,
  BoltIcon,
  StarIcon,
  TrophyIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';

const StakingInfoPage: React.FC = () => {
  const stakingTiers = [
    {
      name: 'Bronze',
      minStake: '1,000 NDH',
      feeDiscount: '25%',
      apyBoost: '1x',
      benefits: ['Basic fee discount', 'Standard support', 'Platform access'],
      color: 'from-amber-700 to-amber-600',
      icon: '🥉',
    },
    {
      name: 'Silver',
      minStake: '10,000 NDH',
      feeDiscount: '50%',
      apyBoost: '1.5x',
      benefits: ['Enhanced fee discount', 'Priority support', 'Early access to auctions', 'Governance voting'],
      color: 'from-slate-400 to-slate-300',
      icon: '🥈',
    },
    {
      name: 'Gold',
      minStake: '50,000 NDH',
      feeDiscount: '75%',
      apyBoost: '2x',
      benefits: ['Premium fee discount', 'VIP support', 'Exclusive drops', 'Curation rights', 'Enhanced governance weight'],
      color: 'from-yellow-400 to-amber-500',
      icon: '🥇',
    },
    {
      name: 'Platinum',
      minStake: '250,000 NDH',
      feeDiscount: '100%',
      apyBoost: '3x',
      benefits: ['Zero platform fees', 'Dedicated account manager', 'Private auctions', 'Revenue sharing', 'Maximum governance power'],
      color: 'from-slate-300 to-indigo-400',
      icon: '💎',
    },
  ];

  const stakingBenefits = [
    {
      icon: CurrencyDollarIcon,
      title: 'Fee Discounts',
      description: 'Reduce platform fees up to 100% based on your staking tier. Platinum members pay zero fees.',
    },
    {
      icon: ChartBarIcon,
      title: 'Staking Rewards',
      description: 'Earn passive NDH rewards proportional to your stake. Higher tiers get APY multipliers.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Governance Power',
      description: 'Vote on platform proposals, parameter changes, and treasury allocations.',
    },
    {
      icon: StarIcon,
      title: 'Early Access',
      description: 'Get priority access to new features, auctions, and exclusive NFT drops.',
    },
    {
      icon: TrophyIcon,
      title: 'Curation Rights',
      description: 'Higher tiers can participate in curation, earning rewards for quality control.',
    },
    {
      icon: GiftIcon,
      title: 'Exclusive Rewards',
      description: 'Receive airdrops, bonus rewards, and special incentives for long-term stakers.',
    },
  ];

  const stakingMechanics = [
    {
      title: 'Flexible Staking',
      description: 'Stake any amount with no minimum lock period. Unstake anytime with a 7-day cooldown.',
      icon: ClockIcon,
    },
    {
      title: 'Compound Rewards',
      description: 'Auto-compound your rewards to maximize APY, or claim them manually at any time.',
      icon: BoltIcon,
    },
    {
      title: 'Secure Contracts',
      description: 'All staking contracts are audited by leading security firms and formally verified.',
      icon: LockClosedIcon,
    },
  ];

  const stats = [
    { label: 'Total Staked', value: '28M NDH' },
    { label: 'Unique Stakers', value: '15,000+' },
    { label: 'Average APY', value: '12%' },
    { label: 'Total Rewards Paid', value: '$8.5M' },
  ];

  const contractInfo = {
    address: '0x4567890123456789012345678901234567890123',
    network: 'Polygon',
    auditedBy: 'CertiK, Trail of Bits',
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <SparklesIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              NDH <span className="text-primary-400">Staking</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Stake your NDH tokens to unlock premium benefits, earn passive rewards, and gain governance power in the TEMPLUM ecosystem.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dashboard/staking"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Start Staking
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <a
                href="#tiers"
                className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                View Tiers
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Staking Benefits</h2>
              <p className="text-gray-400">Why stake NDH? Here's what you unlock.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stakingBenefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors"
                  >
                    <Icon className="w-10 h-10 text-primary-400 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Staking Tiers */}
      <section id="tiers" className="py-16 bg-dark-900/30 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Staking Tiers</h2>
              <p className="text-gray-400">Stake more to unlock greater benefits</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stakingTiers.map((tier, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
                >
                  <div className={`h-2 bg-gradient-to-r ${tier.color}`} />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{tier.icon}</span>
                      <div>
                        <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                        <p className="text-sm text-gray-500">Min: {tier.minStake}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-dark-900/50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Fee Discount</p>
                        <p className="text-lg font-bold text-primary-400">{tier.feeDiscount}</p>
                      </div>
                      <div className="bg-dark-900/50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">APY Boost</p>
                        <p className="text-lg font-bold text-green-400">{tier.apyBoost}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {tier.benefits.map((benefit, bidx) => (
                        <li key={bidx} className="flex items-center gap-2 text-sm text-gray-400">
                          <CheckBadgeIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">How Staking Works</h2>
              <p className="text-gray-400">Simple, secure, and flexible</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {stakingMechanics.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center"
                  >
                    <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Smart Contract Info */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Smart Contract</h2>
              <p className="text-gray-400">Fully audited and secure</p>
            </div>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contract Address</p>
                  <code className="text-primary-400 text-sm break-all">{contractInfo.address}</code>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Network</p>
                  <p className="text-white">{contractInfo.network}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Audited By</p>
                  <p className="text-white">{contractInfo.auditedBy}</p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-dark-700">
                <h4 className="text-white font-semibold mb-3">Contract Code Preview</h4>
                <pre className="bg-dark-900 rounded-lg p-4 text-xs text-gray-400 overflow-x-auto">
{`// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

import "@openzeppelin/contracts/token/ERC20/IERC20.sol";
import "@openzeppelin/contracts/security/ReentrancyGuard.sol";

contract NDHStaking is ReentrancyGuard {
    IERC20 public immutable ndhToken;

    struct Stake {
        uint256 amount;
        uint256 timestamp;
        uint256 tier;
    }

    mapping(address => Stake) public stakes;

    uint256 public constant BRONZE_MIN = 1_000 * 1e18;
    uint256 public constant SILVER_MIN = 10_000 * 1e18;
    uint256 public constant GOLD_MIN = 50_000 * 1e18;
    uint256 public constant PLATINUM_MIN = 250_000 * 1e18;

    function stake(uint256 amount) external nonReentrant {
        require(amount > 0, "Cannot stake 0");
        ndhToken.transferFrom(msg.sender, address(this), amount);
        stakes[msg.sender].amount += amount;
        stakes[msg.sender].tier = _calculateTier(stakes[msg.sender].amount);
        emit Staked(msg.sender, amount);
    }

    function _calculateTier(uint256 amount) internal pure returns (uint256) {
        if (amount >= PLATINUM_MIN) return 4;
        if (amount >= GOLD_MIN) return 3;
        if (amount >= SILVER_MIN) return 2;
        if (amount >= BRONZE_MIN) return 1;
        return 0;
    }
    // ... additional functions
}`}
                </pre>
                <a
                  href="https://github.com/templum-dao/contracts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary-400 text-sm mt-4 hover:underline"
                >
                  View full contract on GitHub <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl p-8 md:p-12 border border-primary-500/30 text-center">
              <SparklesIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start Staking?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Connect your wallet and stake NDH to start earning rewards and unlock premium platform benefits.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/staking"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
                >
                  Go to Staking Dashboard
                </Link>
                <Link
                  to="/ndh-token"
                  className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
                >
                  Get NDH Token
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StakingInfoPage;
